import { ConditionalRender } from '@medeloop/medeloop-components-library';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetDataSourceById } from 'src/api/data-sources-queries';
import {
  findDemographic,
  formatNumberToKMB,
  formatWikiDataForChart,
  getTotalCount,
} from 'src/utils/datasets';
import CumulativeChart from '../charts/cumulative-chart';
import DistributionChart from '../charts/distribution-chart';
import { GraphSkeleton } from '../charts/graph-skeleton';
import MapChart from '../charts/map-chart';
import StatCard from '../charts/stat-card';
import { DataSourceWiki } from 'src/api/data-sources';

export const SubjectCharacteristics = () => {
  const { datasetId } = useParams();

  const { data: dataSource, isLoading } = useGetDataSourceById(datasetId ?? '');

  const localDataSourceWiki = useMemo(
    () => (dataSource?.dataWiki || {}) as DataSourceWiki,
    [dataSource],
  );

  console.log('demographics', localDataSourceWiki?.metadata?.demographics);

  const meanPatientYears = useMemo(
    () =>
      formatNumberToKMB(
        localDataSourceWiki?.metadata?.demographics.find((demo) => demo.metric === 'patient_years')
          ?.mean_patient_years || 0,
      ),
    [localDataSourceWiki],
  );

  const cumulativePatientYears = useMemo(
    () =>
      formatNumberToKMB(
        localDataSourceWiki?.metadata?.demographics.find((demo) => demo.metric === 'patient_years')
          ?.cumulative_patient_years || 0,
      ),
    [localDataSourceWiki],
  );

  const unknownAgeValues = useMemo(
    () =>
      formatNumberToKMB(
        localDataSourceWiki?.metadata?.demographics.find((demo) => demo.metric === 'age')
          ?.distribution?.null || 0,
      ),
    [localDataSourceWiki],
  );

  return (
    <section>
      <ConditionalRender
        assertion={!isLoading}
        fallback={
          <h2 className="font-semibold text-gray-100">
            <GraphSkeleton />
          </h2>
        }
      >
        <ConditionalRender
          assertion={(localDataSourceWiki?.metadata?.demographics ?? []).length > 0}
          fallback={
            <h2 className="font-semibold text-gray-100">
              No subject characteristics data available for this dataset.
            </h2>
          }
        >
          <>
            {localDataSourceWiki?.metadata?.demographics.find(
              (demo) => demo.metric.toLowerCase() === 'geography',
            ) && (
              <div className="py-8 bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued">
                <h2 className="font-semibold text-gray-100 pl-8">Location</h2>
                <div className="flex items-center justify-center ml-16">
                  {(() => {
                    const geographyData = formatWikiDataForChart(
                      localDataSourceWiki,
                      'geography',
                      true, // isCategorical
                      true, // excludeUnknown
                    ).map(({ label, value }) => ({ state: `${label}`, count: value }));

                    const totalCount = getTotalCount(localDataSourceWiki, 'geography');

                    return <MapChart totalCount={totalCount} data={geographyData} />;
                  })()}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-4 pb-8">
              {(meanPatientYears !== '0' || cumulativePatientYears !== '0') && (
                <div className="grid grid-cols-2 gap-4">
                  <StatCard
                    title="Mean Patient Years"
                    value={formatNumberToKMB(
                      localDataSourceWiki?.metadata?.demographics.find(
                        (demo) => demo.metric === 'patient_years',
                      )?.mean_patient_years || 0,
                    )}
                  />
                  <StatCard
                    title="Cumulative Patient Years"
                    value={formatNumberToKMB(
                      localDataSourceWiki?.metadata?.demographics.find(
                        (demo) => demo.metric === 'patient_years',
                      )?.cumulative_patient_years || 0,
                    )}
                  />
                </div>
              )}
              {unknownAgeValues !== '0' && (
                <StatCard
                  title="Unknown Age Values"
                  value={formatNumberToKMB(
                    localDataSourceWiki?.metadata?.demographics.find(
                      (demo) => demo.metric === 'age',
                    )?.distribution?.null || 0,
                  )}
                />
              )}
              {findDemographic(localDataSourceWiki, 'patient_years') && (
                <div className="bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued">
                  <DistributionChart
                    title="Patient Years Distribution"
                    xAxisName="Patient Years"
                    yAxisName="Subjects (n)"
                    data={formatWikiDataForChart(
                      localDataSourceWiki,
                      'patient_years',
                      true, // isCategorical
                      true, // excludeUnknown
                    )}
                    totalCount={getTotalCount(localDataSourceWiki, 'patient_years')}
                    barsTouching={false}
                    isCategorical={true}
                    startColor="#67E8F9"
                    endColor="#164E63"
                    // rotateXAxisTicks={true}
                    showXAxisName={false}
                    sortBars={false}
                    // bottomMargin={50}
                    useKMBFormat={true}
                  />
                </div>
              )}
              {findDemographic(localDataSourceWiki, 'age') && (
                <div className="bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued">
                  <DistributionChart
                    title="Age Distribution"
                    xAxisName="Age (years)"
                    yAxisName="Subjects (n)"
                    data={formatWikiDataForChart(
                      localDataSourceWiki,
                      'age',
                      false, // isCategorical
                      true, // excludeUnknown
                    )}
                    // showBarLabels={false}
                    barsTouching={true}
                    isCategorical={false}
                    binSize={10}
                    // rotateXAxisTicks={true}
                    bottomMargin={0}
                    startColor="#67E8F9"
                    endColor="#164E63"
                    useKMBFormat={true}
                    totalCount={getTotalCount(localDataSourceWiki, 'age')}
                  />
                </div>
              )}
              <div className="bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued">
                <DistributionChart
                  title="Gender Distribution"
                  xAxisName="Gender"
                  yAxisName="Subjects (n)"
                  data={formatWikiDataForChart(
                    localDataSourceWiki,
                    'gender',
                    true, // isCategorical
                    true, // excludeUnknown
                  )}
                  totalCount={getTotalCount(localDataSourceWiki, 'gender')}
                  barsTouching={false}
                  isCategorical={true}
                  startColor="#67E8F9"
                  endColor="#164E63"
                  showXAxisName={false}
                  sortBars={true}
                  useKMBFormat={true}
                />
              </div>
              <div className="bg-background-dark-subdued rounded-lg border border-stroke-on-dark-subdued">
                <DistributionChart
                  title="Race/Ethnicity Distribution"
                  xAxisName="Race/Ethnicity"
                  yAxisName="Subjects (n)"
                  data={formatWikiDataForChart(
                    localDataSourceWiki,
                    'race',
                    true, // isCategorical
                    true, // excludeUnknown
                  )}
                  totalCount={getTotalCount(localDataSourceWiki, 'race')}
                  barsTouching={false}
                  isCategorical={true}
                  startColor="#67E8F9"
                  endColor="#164E63"
                  // rotateXAxisTicks={true}
                  showXAxisName={false}
                  sortBars={true}
                  // bottomMargin={50}
                  useKMBFormat={true}
                />
              </div>
            </div>
            {findDemographic(localDataSourceWiki, 'numbers') && (
              <div className="bg-background-dark-subdued rounded-lg pb-8 mt-8 border border-stroke-on-dark-subdued">
                <CumulativeChart
                  title="Cumulative Subjects"
                  xAxisName="Time period"
                  yAxisName="Subjects (n)"
                  data={[
                    { period: 'Jan', value: 75 },
                    { period: 'Feb', value: 85 },
                    { period: 'Mar', value: 100 },
                    { period: 'Apr', value: 120 },
                    { period: 'May', value: 170 },
                    { period: 'Jun', value: 175 },
                    { period: 'Jul', value: 180 },
                    { period: 'Aug', value: 190 },
                    { period: 'Sep', value: 200 },
                    { period: 'Oct', value: 210 },
                    { period: 'Nov', value: 220 },
                    { period: 'Dec', value: 240 },
                  ]}
                  startColor="#50D6DE"
                  endColor="#50D6DE"
                  useKMBFormat={true}
                />
              </div>
            )}
          </>
        </ConditionalRender>
      </ConditionalRender>
    </section>
  );
};
